import Link from "next/link";
import { useRouter } from "next/router";
import React, { useContext, useState, useEffect } from "react";
import { usePostHog } from 'posthog-js/react';

import { IoBagCheckOutline, IoClose, IoBagHandle, IoFlashSharp } from "react-icons/io5";
import { GiSnail } from "react-icons/gi";
import { FiShoppingCart } from "react-icons/fi";

//internal import
import CartItem from "@component/cart/CartItem";
import LoginModal from "@component/modal/LoginModal";
import { UserContext } from "@context/UserContext";
import { SidebarContext } from "@context/SidebarContext";
import { CartContext } from "@context/CartContext";

const Cart = () => {
  const router = useRouter();
  const [modalOpen, setModalOpen] = useState(false);
  const posthog = usePostHog();
  const { items, isEmpty, cartTotal, inStockItems, preOrderItems } =
    useContext(CartContext);
  const { toggleCartDrawer, closeCartDrawer, cartDrawerOpen } = useContext(SidebarContext);

 const {
  state: { userInfo },
} = useContext(UserContext);

// PostHog Cart Viewed event - only fire when cart drawer is opened
useEffect(() => {
  if (cartDrawerOpen && !isEmpty && items.length > 0 && posthog) {
    posthog.capture('Cart Viewed', {
      cart_id: null, // Add cart ID if available
      products: items.map((item, index) => ({
        product_id: item._id,
        sku: item.sku,
        category: item.parent,
        name: item.title,
        brand: item.brand || null,
        variant: item.variant || null,
        price: item.price,
        quantity: item.quantity,
        coupon: null, // Add coupon if available
        position: index + 1,
        url: `https://www.cubenationshop.com/product/${item.slug}`,
        image_url: item.image || null
      }))
    });
  }
}, [cartDrawerOpen, items, isEmpty, posthog]);

  const handleOpenLogin = () => {
    const prevPath =
      router.asPath.split("?").length === 2
        ? router.asPath.split("?")[0]
        : router.asPath;

    router.push(`${prevPath}/?redirect=/checkout`);
    toggleCartDrawer();
    setModalOpen(!modalOpen);
  };

  const checkoutClass = (
    <button
      onClick={() => {
        closeCartDrawer();
        //handleOpenLogin();
      }}
      className="w-full py-4 px-4 rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 flex items-center justify-between text-sm sm:text-base text-white focus:outline-none transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02] font-medium"
    >
      <span className="align-middle font-medium font-serif">
        Proceed To Checkout
      </span>
      <span className="rounded-lg font-bold font-serif py-2 px-3 bg-white text-red-600 shadow-inner">
        ৳{cartTotal.toFixed(2)}
      </span>
    </button>
  );

  return <>
    {modalOpen && (
      <LoginModal modalOpen={modalOpen} setModalOpen={setModalOpen} />
    )}
    <div className="flex flex-col w-full h-full justify-between items-middle bg-white rounded z-50">
      {/* Cart Header */}
      <div className="w-full flex justify-between items-center relative px-5 py-4 border-b bg-gradient-to-r from-gray-50 to-gray-100 border-gray-100 shadow-sm">
        <h2 className="font-semibold font-serif text-lg m-0 text-gray-700 flex items-center">
          <span className="text-xl mr-2 mb-1 text-red-600">
            <FiShoppingCart />
          </span>
          Shopping Cart
        </h2>
        <button
          onClick={closeCartDrawer}
          className="inline-flex text-base items-center justify-center text-gray-500 p-2 focus:outline-none transition-all duration-300 hover:text-red-500 hover:scale-110"
        >
          <IoClose />
          <span className="font-sans text-sm text-gray-500 hover:text-red-500 ml-1">
            Close
          </span>
        </button>
      </div>

      {/* Cart Body */}
      <div className="overflow-y-scroll flex-grow scrollbar-hide w-full max-h-full">
        {isEmpty && (
          <div className="flex flex-col h-full justify-center">
            <div className="flex flex-col items-center p-8">
              <div className="flex justify-center items-center w-24 h-24 rounded-full bg-red-50 mb-4 shadow-inner">
                <span className="text-red-500 text-4xl block animate-pulse">
                  <IoBagHandle />
                </span>
              </div>
              <h3 className="font-serif font-semibold text-gray-700 text-lg pt-3">
                Your cart is empty
              </h3>
              <p className="px-12 text-center text-sm text-gray-500 pt-2">
                No items added in your cart. Please add product to your cart list.
              </p>
            </div>
          </div>
        )}

        {inStockItems.length > 0 ? (
          <div className="mb-3">
            <div className="text-sm grid grid-cols-2 bg-gradient-to-r from-gray-50 to-gray-100 p-3 md:p-4 rounded-md mx-2 mt-2 shadow-sm">
              <h4 className="font-bold flex items-center">
                <span className="text-xl mr-3 text-red-500"><IoFlashSharp className="animate-pulse" /></span>
                <span className="text-sm item-center text-gray-700">In-stock Items</span>
              </h4>
              <h4 className="text-end italic text-gray-600 font-medium">Delivery in 1-3 days</h4>
            </div>

            {inStockItems?.map((item, i) => (
              <CartItem
                key={i + 1}
                item={item}
                checkoutPage={true}
                status={"inStock"}
              />
            ))}
          </div>
        ) : null}

        {preOrderItems.length > 0 ? (
          <div className="mb-3">
            <div className="text-sm grid grid-cols-2 bg-gradient-to-r from-gray-50 to-gray-100 p-3 md:p-4 rounded-md mx-2 mt-2 shadow-sm">
              <h4 className="font-bold flex items-center">
                <span className="text-xl mr-3 text-blue-500"><GiSnail /></span>
                <span className="text-sm item-center text-gray-700">Pre-order Items</span>
              </h4>
              <h4 className="text-end italic text-gray-600 font-medium">Delivery in 7-35 days</h4>
            </div>

            {preOrderItems?.map((item, i) => (
              <CartItem
                key={i + 1}
                item={item}
                checkoutPage={true}
                status={"preStock"}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* Free Shipping Notice */}
      {!isEmpty && (
        <>
          {1000 - cartTotal > 0 ? (
            <div className="mx-5 my-2 py-3 px-4 rounded-xl shadow-sm border border-red-100 text-gray-700 font-medium flex items-center justify-between bg-gradient-to-r from-red-50 to-red-100 text-sm focus:outline-none transition-all duration-300">
              <span>Shop ৳{(1000 - cartTotal).toFixed(0)} more for free delivery</span>
              <div className="w-16 bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-gradient-to-r from-red-500 to-red-600 h-2.5 rounded-full"
                  style={{ width: `${(cartTotal / 1000) * 100}%` }}
                ></div>
              </div>
            </div>
          ) : (
            <div className="mx-5 my-2 py-3 px-4 rounded-xl shadow-sm border border-green-100 text-gray-700 font-medium flex items-center justify-between bg-gradient-to-r from-green-50 to-green-100 text-sm focus:outline-none transition-all duration-300">
              <span>Free delivery unlocked!</span>
              <div className="ml-2 text-green-500 text-lg">✓</div>
            </div>
          )}
        </>
      )}

      {/* Checkout Button */}
      <div className="mx-5 my-4">        {items.length <= 0 ? (
          checkoutClass
        ) : (
          <span>
            {!userInfo ? (
              <div onClick={handleOpenLogin}>{checkoutClass}</div>
            ) : (
              <Link href="/checkout">
                <div>{checkoutClass}</div>
              </Link>
            )}
          </span>
        )}
      </div>
    </div>
  </>;
};

export default Cart;
